import { FC, Fragment, useState } from 'react';
import { connect } from 'react-redux';
import { AnyAction, bindActionCreators, Dispatch } from 'redux';
import { FormattedMessage } from 'react-intl';
import * as modalWindowActions from 'root/modal/modalWindowActions';
import * as vehiclesActions from 'features/vehicles/vehiclesActions';
import * as Sentry from '@sentry/react';
import { DeleteDeviceErrorKey, LocalizedLicensePlate, ReduxState, Status, Vehicle } from 'types';
import { Api } from 'api';
import { STATUS_INITIAL } from 'myParkdUtils/constants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCar, faCheckCircle, faTimesCircle, faWarning } from '@fortawesome/free-solid-svg-icons';
import LoadingSpinner from 'components/LoadingSpinner';


export const containsBlockingInvalidationError = (errorKeys: DeleteDeviceErrorKey[]) => {
    return errorKeys && errorKeys.some((errorKey) => ['UNKNOWN_DEVICE', 'CANNOT_UNCLAIM_DURING_ACTIVE_SESSION', 'UNAUTHORIZED'].includes(errorKey));
};

export const isClaimedDevice = (errorKeys: DeleteDeviceErrorKey[]) => {
    return errorKeys && errorKeys.includes('DEVICE_CLAIMED');
};


export const ListDeleteVehicleWarnings = ({ errorKeys }: { errorKeys: DeleteDeviceErrorKey[] }) => {
    const mapErrorKeyToTranslationKey = (errorKey: DeleteDeviceErrorKey): string => {
        switch (errorKey) {
            case 'CANNOT_UNCLAIM_DURING_ACTIVE_SESSION':
                return 'DELETE_VEHICLE_MODAL_ERROR_CANNOT_UNCLAIM_DURING_ACTIVE_SESSION';
            case 'DEVICE_CLAIMED':
                return 'DELETE_VEHICLE_MODAL_ERROR_DEVICE_CLAIMED';
            case 'DEVICE_RECENT_EVENT':
                return 'DELETE_VEHICLE_MODAL_ERROR_DEVICE_RECENT_EVENT';
            case 'DEVICE_RECENT_SESSION':
                return 'DELETE_VEHICLE_MODAL_ERROR_DEVICE_RECENT_SESSION';
            case 'UNAUTHORIZED':
                return 'DELETE_VEHICLE_MODAL_ERROR_UNAUTHORIZED';
            case 'UNKNOWN_DEVICE':
                return 'DELETE_VEHICLE_MODAL_ERROR_UNKNOWN_DEVICE';
            default:
                Sentry.captureMessage(`Unhandled errorKey: ${errorKey} found for vehicle invalidation`, 'warning');
                return 'DELETE_VEHICLE_MODAL_ERROR_UNKNOWN_ERROR_KEY';
        }
    }

    return errorKeys && errorKeys.length ?
        (<ul className="list-warnings" data-testid='list-delete-vehicle-warnings'>
            {errorKeys?.map((errorKey: DeleteDeviceErrorKey, i: number) => {
                const isBlockingError = containsBlockingInvalidationError([errorKey]);
                return (
                    <li className={isBlockingError ? 'red' : 'orange'} key={`errorkey_${i}`}>
                        <FontAwesomeIcon data-testid="errorkey-icon" icon={isBlockingError ? faTimesCircle : faWarning} size='sm' style={{ marginRight: '0.5em' }} />
                        <FormattedMessage id={mapErrorKeyToTranslationKey(errorKey)} />
                    </li>
                );
            })}
        </ul>
        ) : null
}


type ForceDeleteProps = {
    localizedLicensePlate: LocalizedLicensePlate,
    errorKeys: DeleteDeviceErrorKey[],
    deleteVehicle: Function,
    closeModal: Function
}

const ForceDeleteContent = (props: ForceDeleteProps) => {
    const containsBlockingError = containsBlockingInvalidationError(props.errorKeys);

    return (
        <Fragment>
            <div>
                <FormattedMessage id="DELETE_VEHICLE_MODAL_TEXT_DELETE_FAILED"
                    values={{
                        licensePlate: props.localizedLicensePlate.licensePlate,
                        countryCode: props.localizedLicensePlate.countryCode
                    }}
                />

                <ListDeleteVehicleWarnings errorKeys={props.errorKeys} />

                <span>
                    {containsBlockingError
                        ? <FormattedMessage id="DELETE_VEHICLE_MODAL_TEXT_SKIP_VALIDATIONS_NOT_ALLOWED" />
                        : <FormattedMessage id="DELETE_VEHICLE_MODAL_TEXT_SKIP_VALIDATIONS_ALLOWED" />
                    }
                </span>
            </div>
            <div className="row-7" style={{ marginTop: 10 }}>
                <div className="col-6">
                    <button data-testid='btn-cancel-skip-validations' className={containsBlockingError ? "btn-blue" : "btn-cancel"} onClick={() => props.closeModal()}>
                        <FormattedMessage id="GLOBAL_CANCEL" />
                    </button>
                </div>
                <div className="col-6">
                    <button data-testid='btn-delete-skip-validations' className="btn-red" disabled={containsBlockingError} onClick={() => props.deleteVehicle(true)}>
                        <FormattedMessage id="GLOBAL_DELETE" />
                    </button>
                </div>
            </div>
        </Fragment>
    )
};


export const DeleteVehicleModal: FC<Props> = (props) => {
    const [actionStatus, setActionStatus] = useState<Status>(STATUS_INITIAL);

    const errorKeys = actionStatus.error?.response?.body?.errorKeys;

    const deleteVehicle = async (skipValidations: boolean) => {
        try {
            setActionStatus({ loading: true, succeeded: false, error: null });
            await Api.deleteVehicle(props.token, props.vehicle.localizedLicensePlate.countryCode, props.vehicle.localizedLicensePlate.licensePlate, skipValidations);
            setActionStatus({ loading: false, succeeded: true, error: null });
            props.removeVehicleRowAfterDeleted(props.vehicle);
        } catch (error: any) {
            setActionStatus({ loading: false, succeeded: false, error: error });
        }
    };

    return (
        <div className="panel radius-rectangle full-width-form confirmation-modal">
            <div className="panel-header">
                <h2 className="bold-title">
                    <FontAwesomeIcon icon={faCar} size='sm' /><FormattedMessage id="DELETE_VEHICLE_MODAL_TITLE" />
                </h2>
            </div>
            <div className="panel-content align-left">
                {actionStatus.loading || actionStatus.succeeded || actionStatus.error ? (
                    <div>
                        {actionStatus.loading && <LoadingSpinner message={<FormattedMessage id='GLOBAL_DELETING' />} />}
                        {actionStatus.succeeded && (
                            <Fragment>
                                <div data-testid='confirmation-modal-success-message'>
                                    <FontAwesomeIcon icon={faCheckCircle} size='lg' style={{ marginRight: '0.5em', color: '#2fbb7c' }} />
                                    <FormattedMessage id="DELETE_VEHICLE_MODAL_TEXT_DELETE_SUCCESS" />
                                </div>
                                <button className="btn-blue btn-full-width offset-top-15" onClick={() => props.closeModal()}>
                                    <FormattedMessage id="GLOBAL_CLOSE" />
                                </button>
                            </Fragment>
                        )}
                        {actionStatus.error && (
                            <ForceDeleteContent
                                localizedLicensePlate={props.vehicle.localizedLicensePlate}
                                errorKeys={errorKeys}
                                deleteVehicle={deleteVehicle}
                                closeModal={props.closeModal}
                            />
                        )}
                    </div>
                )
                    :
                    (
                        <Fragment>
                            <FormattedMessage id="DELETE_VEHICLE_MODAL_TEXT_DELETE_VEHICLE"
                                values={{
                                    licensePlate: props.vehicle.localizedLicensePlate.licensePlate,
                                    countryCode: props.vehicle.localizedLicensePlate.countryCode
                                }}
                            />

                            <div className="row-7">
                                <div className="col-6">
                                    <button className="btn-cancel" onClick={() => props.closeModal()}>
                                        <FormattedMessage id="GLOBAL_CANCEL" />
                                    </button>
                                </div>
                                <div className="col-6">
                                    <button className="btn-red" onClick={() => deleteVehicle(false)}>
                                        <FormattedMessage id="GLOBAL_DELETE" />
                                    </button>
                                </div>
                            </div>
                        </Fragment>
                    )}
            </div>
        </div>
    );
};


type OwnProps = { vehicle: Vehicle };
type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = StateProps & DispatchProps & OwnProps;

function mapStateToProps(state: ReduxState) {
    return {
        token: state.auth.token,
    };
}

function mapDispatchToProps(dispatch: Dispatch<AnyAction>) {
    return bindActionCreators({
        closeModal: modalWindowActions.closeModal,
        removeVehicleRowAfterDeleted: vehiclesActions.removeVehicleRowAfterDeleted,
    }, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(DeleteVehicleModal);
